import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/cn";
import {
  getNavConfig,
  isActivePath,
  loadExpandedSections,
  persistExpandedSections,
  sectionContainsPath,
  type NavLink,
  type NavSection,
} from "@/lib/nav-items";

function NavLinkRow({
  item,
  pathname,
  search,
  dense,
  onNavigate,
  iconOnly,
}: {
  item: NavLink;
  pathname: string;
  search: string;
  dense?: boolean;
  onNavigate?: () => void;
  iconOnly?: boolean;
}) {
  const active = isActivePath(item.href, pathname, search);
  const Icon = item.icon;
  const linkRef = useRef<HTMLAnchorElement>(null);
  const tipId = useId();
  const [tipPos, setTipPos] = useState<{ top: number; left: number } | null>(
    null,
  );

  function showTip() {
    const el = linkRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  }

  function hideTip() {
    setTipPos(null);
  }

  return (
    <>
      <Link
        ref={linkRef}
        to={item.href}
        onClick={onNavigate}
        aria-label={iconOnly ? item.label : undefined}
        aria-describedby={iconOnly && tipPos ? tipId : undefined}
        aria-current={active ? "page" : undefined}
        onMouseEnter={iconOnly ? showTip : undefined}
        onMouseLeave={iconOnly ? hideTip : undefined}
        onFocus={iconOnly ? showTip : undefined}
        onBlur={iconOnly ? hideTip : undefined}
        className={cn(
          "flex items-center rounded-md text-sm transition-colors",
          iconOnly
            ? "justify-center px-0 py-2.5"
            : dense
              ? "gap-3 px-3 py-1.5"
              : "gap-3 px-3 py-2",
          active
            ? "bg-brand-soft text-brand"
            : "text-muted hover:bg-surface-hover hover:text-foreground",
        )}
      >
        <Icon className={dense && !iconOnly ? "size-3.5" : "size-4"} />
        {!iconOnly && (
          <>
            <span className="truncate">{item.label}</span>
            {item.chip && (
              <span className="ml-auto shrink-0 rounded-full border border-brand/30 bg-brand-soft px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand">
                {item.chip}
              </span>
            )}
          </>
        )}
      </Link>
      {iconOnly && tipPos &&
        createPortal(
          <span
            id={tipId}
            role="tooltip"
            style={{ top: tipPos.top, left: tipPos.left }}
            className="pointer-events-none fixed z-[200] -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-foreground shadow-xl ring-1 ring-black/5 dark:ring-white/10"
          >
            {item.label}
          </span>,
          document.body,
        )}
    </>
  );
}

function CollapsedSection({
  section,
  pathname,
  search,
  onNavigate,
  openSectionId,
  onOpenChange,
}: {
  section: NavSection;
  pathname: string;
  search: string;
  onNavigate?: () => void;
  openSectionId: string | null;
  onOpenChange: (id: string | null) => void;
}) {
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const tipId = useId();
  const [tipPos, setTipPos] = useState<{ top: number; left: number } | null>(
    null,
  );
  const SectionIcon = section.icon;
  const hasActive = sectionContainsPath(section, pathname, search);
  const open = openSectionId === section.id;

  function updatePos() {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.right + 4 });
  }

  function showTip() {
    if (open) return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  }

  function hideTip() {
    setTipPos(null);
  }

  useEffect(() => {
    if (!open) return;
    updatePos();

    function syncPosition() {
      updatePos();
    }

    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);
    return () => {
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onOpenChange(null);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onOpenChange(null);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={section.label}
        aria-describedby={tipPos ? tipId : undefined}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          if (open) {
            onOpenChange(null);
            return;
          }
          updatePos();
          onOpenChange(section.id);
        }}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onFocus={showTip}
        onBlur={hideTip}
        className={cn(
          "flex w-full items-center justify-center rounded-md py-2.5 transition-colors",
          hasActive || open
            ? "bg-brand-soft text-brand"
            : "text-muted-foreground hover:bg-surface-hover hover:text-foreground",
        )}
      >
        <SectionIcon className="size-4" />
      </button>
      {tipPos && !open &&
        createPortal(
          <span
            id={tipId}
            role="tooltip"
            style={{ top: tipPos.top, left: tipPos.left }}
            className="pointer-events-none fixed z-[200] -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-foreground shadow-xl ring-1 ring-black/5 dark:ring-white/10"
          >
            {section.label}
          </span>,
          document.body,
        )}
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="menu"
            aria-label={section.label}
            style={{ top: pos.top, left: pos.left }}
            className="fixed z-[100] min-w-[200px] rounded-md border border-border bg-popover py-1.5 shadow-xl ring-1 ring-black/5 dark:ring-white/10"
          >
            <p className="border-b border-border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {section.label}
            </p>
            <div className="flex flex-col gap-0.5 p-1">
              {section.items.map((item) => (
                <NavLinkRow
                  key={item.href}
                  item={item}
                  pathname={pathname}
                  search={search}
                  dense
                  onNavigate={() => {
                    onOpenChange(null);
                    onNavigate?.();
                  }}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export function SideNav({
  onNavigate,
  mobile,
  collapsed = false,
}: {
  onNavigate?: () => void;
  mobile?: boolean;
  collapsed?: boolean;
}) {
  const { pathname, search } = useLocation();
  const nav = getNavConfig();
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    loadExpandedSections(pathname, search),
  );
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);

  useEffect(() => {
    setExpanded((prev) => {
      const active = nav.sections.find((s) =>
        sectionContainsPath(s, pathname, search),
      );
      if (!active || prev.has(active.id)) return prev;
      const next = new Set(prev);
      next.add(active.id);
      persistExpandedSections(next);
      return next;
    });
  }, [pathname, search]);

  useEffect(() => {
    setOpenSectionId(null);
  }, [pathname, search, collapsed]);

  function toggleSection(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistExpandedSections(next);
      return next;
    });
  }

  const iconOnly = collapsed && !mobile;

  return (
    <nav
      className={cn(
        "flex flex-col",
        mobile
          ? "gap-1.5 [&_a]:min-h-[52px] [&_a]:rounded-xl [&_a]:text-base [&_a]:font-semibold [&_a_svg]:size-5 [&_button]:min-h-[52px] [&_button]:rounded-xl [&_button]:text-base"
          : "gap-0.5",
      )}
    >
      {nav.pins.map((item) => (
        <NavLinkRow
          key={item.label}
          item={item}
          pathname={pathname}
          search={search}
          onNavigate={onNavigate}
          iconOnly={iconOnly}
        />
      ))}

      {nav.sections.length > 0 && (
        <>
          <div className="my-2 border-t border-border" />
          {iconOnly
            ? nav.sections.map((section) => (
                <CollapsedSection
                  key={section.id}
                  section={section}
                  pathname={pathname}
                  search={search}
                  onNavigate={onNavigate}
                  openSectionId={openSectionId}
                  onOpenChange={setOpenSectionId}
                />
              ))
            : nav.sections.map((section) => {
                const open = expanded.has(section.id);
                const SectionIcon = section.icon;
                const hasActive = sectionContainsPath(section, pathname, search);
                return (
                  <div key={section.id} className="mb-1">
                    <button
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      aria-expanded={open}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors",
                        hasActive
                          ? "text-foreground"
                          : "text-muted-foreground hover:bg-surface-hover hover:text-foreground",
                      )}
                    >
                      <SectionIcon className="size-3.5 shrink-0" />
                      <span className="flex-1 truncate">{section.label}</span>
                      <ChevronDown
                        className={cn(
                          "size-3.5 shrink-0 text-muted-foreground transition-transform",
                          open && "rotate-180",
                        )}
                      />
                    </button>
                    {open && (
                      <div className="ml-1 flex flex-col gap-0.5 border-l border-border pl-2">
                        {section.items.map((item) => (
                          <NavLinkRow
                            key={item.href}
                            item={item}
                            pathname={pathname}
                            search={search}
                            dense
                            onNavigate={onNavigate}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
        </>
      )}

      {nav.footer.length > 0 && (
        <>
          <div className="my-2 border-t border-border" />
          {/* Secondary destinations — present, but visually quieter than pins. */}
          <div className={mobile ? undefined : "text-xs [&_svg]:size-3.5"}>
            {nav.footer.map((item) => (
              <NavLinkRow
                key={item.href}
                item={item}
                pathname={pathname}
                search={search}
                dense={!mobile}
                onNavigate={onNavigate}
                iconOnly={iconOnly}
              />
            ))}
          </div>
        </>
      )}
    </nav>
  );
}
